/**
 * AndoAiButton — la CTA "lo fa Ando": l'azione che passa la palla all'AI
 * ("Scrivi con Ando", "Genera bozza", …). È la firma visiva del brand nei
 * punti in cui l'utente delega, quindi si distingue dalle azioni normali
 * senza agitarsi: a riposo è un ghost pulito, all'hover compaiono il bordo
 * caldo fermo e un singolo lampo bianco che attraversa la pill — niente
 * animazioni in loop in una barra che ospita altre cinque azioni.
 *
 * Icona: il blob a matita di Ando (`LineBlobGlyph`), non la sparkle. La
 * sparkle dice "c'è dell'AI" ed è di tutti; il blob dice "c'è **Ando**" ed è
 * lo stesso segno del login e del loader. Il blob è fermo finché non lo
 * tocchi: si aggancia da sé all'hover/focus del bottone e prende vita per il
 * tempo che serve, senza che questo componente tenga alcuno stato.
 *
 * Forma: di default segue il raggio di sistema (`rounded-md`, 10px) perché
 * dentro una action bar la forma dev'essere la costante — a distinguere la
 * CTA bastano bordo, lampo e blob. `shape="pill"` esiste per le superfici
 * dove la CTA sta da sola (hero, empty-state, barre flottanti) e la pill non
 * litiga con i bottoni vicini.
 *
 * Misure: font (`text-[12px] font-medium`), line-height ereditato e altezza
 * (`py-1.5`) sono quelli delle altre azioni della barra — allineati, non
 * "quasi". Il respiro in più la CTA se lo prende in orizzontale (`px-5`, gap
 * 2.5), dove non sfalsa la riga e dà aria al bordo acceso.
 *
 * Bordo (`.ando-beam`) e lampo (`.ando-sheen`) vivono in
 * `packages/ui/src/styles.css`: servono maschera anulare e selettori
 * `:hover`/`:focus-visible` sull'host `.ando-ai-cta`, non esprimibili con
 * le utility Tailwind.
 */

import { Loader2 } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";
import { LineBlobGlyph } from "./blob/LineBlobGlyph";

export type AndoAiButtonShape = "default" | "pill";

export interface AndoAiButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly children: React.ReactNode;
  /** Raggio: di sistema (default) o pill piena per superfici isolate. */
  readonly shape?: AndoAiButtonShape;
  /** Ando sta lavorando: il blob diventa spinner (il testo lo passa l'host). */
  readonly pending?: boolean;
}

export const AndoAiButton: React.FC<AndoAiButtonProps> = ({
  children,
  className,
  shape = "default",
  pending = false,
  type = "button",
  ...rest
}) => {
  return (
    <button
      type={type}
      aria-busy={pending || undefined}
      className={cn(
        "ando-ai-cta group/ai relative isolate inline-flex items-center gap-2.5 px-5 py-1.5",
        shape === "pill" ? "rounded-full" : "rounded-md",
        // Niente `leading-none`: le azioni della barra ereditano line-height 1.5
        // dal preflight, e con la riga più corta la CTA restava 4px più bassa.
        "font-medium text-[12px] text-ando-orange",
        "transition-[box-shadow,transform,color] duration-200 ease-out",
        "hover:text-ando-orange-hover hover:shadow-[0_4px_16px_-6px_rgba(255,122,26,0.45)]",
        "active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className="-z-10 pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(120%_150%_at_50%_135%,rgba(255,122,26,0.16),rgba(255,122,26,0.04)_55%,transparent_78%)] opacity-0 transition-opacity duration-300 group-hover/ai:opacity-100"
      />
      <span aria-hidden className="ando-sheen" />
      <span aria-hidden className="ando-beam" />
      {/* Stessa impronta nei due stati: 2px di differenza farebbero
          "saltare" il testo quando parte la generazione. */}
      {pending ? (
        <Loader2 className="h-[18px] w-[18px] shrink-0 animate-spin" strokeWidth={2} aria-hidden />
      ) : (
        // Nessun `active`: il glifo si aggancia da sé all'hover/focus del bottone.
        <LineBlobGlyph size={18} />
      )}
      <span>{children}</span>
    </button>
  );
};

AndoAiButton.displayName = "AndoAiButton";
